import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui";
import { TriangleAlert } from "@/icons";

// A dynamic import() that 404'd — almost always a route chunk a fresh deploy
// replaced. Messages vary by engine: iOS/Safari "Importing a module script
// failed.", Chrome "Failed to fetch dynamically imported module", Firefox
// "error loading dynamically imported module", plus Vite's CSS preload variant.
// `main.tsx` already auto-reloads once via the `vite:preloadError` event; this
// boundary is the backstop for the guarded second hit, so it offers a manual
// reload rather than the dead `reset()` (which just re-imports the same chunk).
const STALE_CHUNK_RE =
  /dynamically imported module|importing a module script|module script failed|unable to preload css/i;

// Returns catalog KEYS (not resolved copy) so the component resolves them via
// t() in the active language — i18n.ts is a side-effect import initialized
// before render (main.tsx), so useTranslation is safe even inside the boundary.
function messageKeysFor(status?: number): { titleKey: string; subKey: string } {
  if (status === 429) return { titleKey: "errorScreen.tooManyTitle", subKey: "errorScreen.tooManySub" };
  if (status && status >= 500) return { titleKey: "errorScreen.serverTitle", subKey: "errorScreen.serverSub" };
  if (status === 401 || status === 403) return { titleKey: "errorScreen.unauthorizedTitle", subKey: "errorScreen.unauthorizedSub" };
  return { titleKey: "errorScreen.genericTitle", subKey: "errorScreen.genericSub" };
}

export function ErrorScreen({ error, reset }: { error: unknown; reset?: () => void }) {
  const { t } = useTranslation("common");
  const status = (error as { status?: number } | null)?.status;
  const detail = (error as Error | null)?.message ?? (error != null ? String(error) : "");
  const isStaleChunk = STALE_CHUNK_RE.test(detail);
  const keys = isStaleChunk
    ? { titleKey: "errorScreen.updateTitle", subKey: "errorScreen.updateSub" }
    : messageKeysFor(status);
  const title = t(keys.titleKey);
  const sub = t(keys.subKey);
  return (
    <div className="grid min-h-dvh place-items-center bg-bg p-6 text-center text-fg">
      <div className="w-full max-w-md">
        <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-danger/15 text-danger">
          <TriangleAlert className="h-6 w-6" />
        </div>
        <h1 className="font-display text-xl font-bold">{title}</h1>
        <p className="mt-1 text-sm text-fg-muted">{sub}</p>
        <div className="mt-5 flex justify-center gap-2">
          {isStaleChunk ? (
            <Button onClick={() => window.location.reload()}>{t("errorScreen.reload")}</Button>
          ) : (
            reset && <Button onClick={reset}>{t("errorScreen.tryAgain")}</Button>
          )}
          <Button variant="outline" onClick={() => window.location.assign("/")}>
            {t("errorScreen.backToMarkets")}
          </Button>
        </div>
        {detail && (
          <details className="mt-5 text-left">
            <summary className="cursor-pointer text-xs text-fg-subtle">{t("errorScreen.technicalDetails")}</summary>
            <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-surface-2 p-2 text-2xs text-fg-muted">{detail}</pre>
          </details>
        )}
      </div>
    </div>
  );
}
